import { isLanguageCode, LanguageCode } from '@/ifaces/language';
import { useEffect, useRef } from 'react';
import {
  CustomEventNames,
  LanguageCodeChangeEvent,
  SetEntitlementsEvent,
} from '../util/custom-events';
import { Entitlements } from '@/ifaces/entitlements';

export function useLanguageSwitcher(props: { onHide(): void; onShow(): void }) {
  const ref = useRef({ hide: props.onHide, show: props.onShow });
  ref.current.hide = props.onHide;
  ref.current.show = props.onShow;
  useEffect(() => {
    const handleLanguageCodeHide = () => ref.current.hide();
    const handleLanguageCodeShow = () => ref.current.show();
    window.addEventListener(CustomEventNames.HideLanguageSwitcher, handleLanguageCodeHide);
    window.addEventListener(CustomEventNames.ShowLanguageSwitcher, handleLanguageCodeShow);
    return () => {
      window.removeEventListener(CustomEventNames.HideLanguageSwitcher, handleLanguageCodeHide);
      window.removeEventListener(CustomEventNames.ShowLanguageSwitcher, handleLanguageCodeShow);
    };
  }, []);
}

export function useLanguageCode(props: { onChange(code: LanguageCode): void }) {
  const ref = useRef({ fn: props.onChange });
  ref.current.fn = props.onChange;
  useEffect(() => {
    const handleLanguageCodeChange = (event: Event) => {
      const code = (event as LanguageCodeChangeEvent).detail?.languageCode;
      if (isLanguageCode(code)) ref.current.fn(code);
    };
    window.addEventListener(CustomEventNames.ChangeLanguageCode, handleLanguageCodeChange);
    return () => {
      window.removeEventListener(CustomEventNames.ChangeLanguageCode, handleLanguageCodeChange);
    };
  }, []);
}

export function useFooterPromoUrl(props: { onShow(promoUrl: string): void }) {
  const ref = useRef({ fn: props.onShow });
  ref.current.fn = props.onShow;
  useEffect(() => {
    const handleShowPromoFooter = (event: Event) => {
      const promoUrl = (event as CustomEvent<{ promoUrl: string }>).detail?.promoUrl;
      if (promoUrl) ref.current.fn(promoUrl);
    };
    window.addEventListener(CustomEventNames.ShowPromoFooter, handleShowPromoFooter);
    return () => {
      window.removeEventListener(CustomEventNames.ShowPromoFooter, handleShowPromoFooter);
    };
  }, []);
}

export function useEntitlements(props: { onSet(entitlements: Entitlements): void }) {
  const ref = useRef({ fn: props.onSet });
  ref.current.fn = props.onSet;
  useEffect(() => {
    const handleEntitlementsUpdate = (event: Event) => {
      const entitlements = (event as SetEntitlementsEvent).detail?.entitlements;
      if (entitlements) ref.current.fn(entitlements);
    };
    window.addEventListener(CustomEventNames.SetEntitlements, handleEntitlementsUpdate);
    return () => {
      window.removeEventListener(CustomEventNames.SetEntitlements, handleEntitlementsUpdate);
    };
  }, []);
}
